import { isCancelledError, type QueryClient } from '@tanstack/react-query'
import { redirect } from '@tanstack/react-router'

import { SCREEN_PATH } from '@/features/documents'
import { documentQuery } from '@/lib/api/queries/documents'
import { screenFor, type Screen } from '@/lib/domain/status'

/** How long a guard keeps joining restarted fetches before it gives up. */
const CANCELLED_PATIENCE_MS = 10_000

/**
 * The document for a route's beforeLoad. An SSE event that invalidates the document while this
 * fetch is in flight cancels it and starts a new one for the mounted screen; letting that
 * cancellation reject beforeLoad shows "This screen couldn't be shown." (seen against the real
 * server when preparation finishes: the status change and 43 render events arrive together).
 * The event has already patched the cached document, and the live status guard follows any later
 * change, so a cached copy is the answer; without one, the guard joins the restarted fetch.
 */
export async function fetchDocument(queryClient: QueryClient, docId: string, now: () => number = Date.now) {
  const query = documentQuery(docId)
  const deadline = now() + CANCELLED_PATIENCE_MS
  for (;;) {
    try {
      return await queryClient.fetchQuery(query)
    } catch (e) {
      if (!isCancelledError(e)) throw e
      const cached = queryClient.getQueryData(query.queryKey)
      if (cached) return cached
      if (now() > deadline) throw e
      // Yield a macrotask so the invalidation's own refetch has started before joining it.
      await new Promise((resolve) => setTimeout(resolve, 20))
    }
  }
}

/**
 * Each document screen's beforeLoad: if the document belongs on another screen (for example /edit
 * while it is Translating), go there instead, replacing the history entry.
 */
export async function guardScreen(queryClient: QueryClient, docId: string, screen: Screen): Promise<void> {
  const doc = await fetchDocument(queryClient, docId)
  const target = screenFor(doc)
  if (target !== screen) throw redirect({ to: SCREEN_PATH[target], params: { docId }, replace: true })
}

/**
 * The Images and Texts steps serve a document from its draft to done (pictures and translations
 * are checked before and after translating); only one still being prepared goes elsewhere.
 */
export async function guardStep(queryClient: QueryClient, docId: string): Promise<void> {
  const doc = await fetchDocument(queryClient, docId)
  if (screenFor(doc) === 'preparing') throw redirect({ to: SCREEN_PATH.preparing, params: { docId }, replace: true })
}
